import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Button, Box, TextField, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Typography, Dialog,
  DialogActions, DialogContent, DialogTitle, MenuItem, Select, FormControl, InputLabel,
  InputAdornment, useMediaQuery, Card, CardContent, Grid
} from '@mui/material';
import { Search, FilterList } from '@mui/icons-material';
import { getOrderAction, createOrderAction, clearCreateOrderData, updateOrderAction, clearupdateOrderAction, deleteOrderAction, cleardeleteOrderAction, getMachineAction } from '../../store/actions';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';

const OrdersManagement = () => {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width: 899px)');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newOrder, setNewOrder] = useState({ machineId: '', clientName: '', quantity: '' });
  const [errorMessage, setErrorMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editOrderId, setEditOrderId] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const [statusFilter, setStatusFilter] = useState('All');

  // Get orders data from Redux store
  const { successData: ordersData } = useSelector((state) => state.getOrderReducer);
  const { successData: createOrderSuccessData, error: createOrderError } = useSelector((state) => state.createOrderReducer);
  const { successData: updateOrderSuccessData, error: updateOrderError } = useSelector((state) => state.updateOrderReducer);
  const { successData: deleteOrderSuccessData, error: deleteOrderError } = useSelector((state) => state.deleteOrderReducer);
  const { successData: machinesData } = useSelector((state) => state.getMachineReducer);

  const handleCreateOrder = () => {
    setIsEditing(false);
    setEditOrderId(null);
    setNewOrder({ machineId: '', clientName: '', quantity: '' });
    setIsModalOpen(true);
    setErrorMessage('');
  };

  const handleEditOrder = (order) => {
    setIsEditing(true);
    setEditOrderId(order.id);
    setNewOrder({
      machineId: order.machine_id.toString(),
      clientName: order.client_name,
      quantity: order.quantity.toString()
    });
    setIsModalOpen(true);
    setErrorMessage('');
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setNewOrder({ machineId: '', clientName: '', quantity: '' });
    setErrorMessage('');
    setIsEditing(false);
    setEditOrderId(null);
  };

  const handleSaveOrder = () => {
    if (!newOrder.machineId || !newOrder.clientName || !newOrder.quantity) {
      setErrorMessage('Please fill in all fields!');
      return;
    }

    const orderData = {
      machine_id: parseInt(newOrder.machineId),
      client_name: newOrder.clientName,
      quantity: parseInt(newOrder.quantity)
    };

    if (isEditing && editOrderId) {
      dispatch(updateOrderAction({
        endPoint: `admin/order/${editOrderId}`,
        ...orderData,
      }));
    } else {
      dispatch(createOrderAction(orderData));
    }

    setIsModalOpen(false);
    setNewOrder({ machineId: '', clientName: '', quantity: '' });
    setErrorMessage('');
    setIsEditing(false);
    setEditOrderId(null);
  };

  // Handle status filter change
  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  useEffect(() => {
    dispatch(getMachineAction());
  }, [dispatch]);

  useEffect(() => {
    if (createOrderSuccessData) {
      dispatch(getOrderAction());
      toast.success('Order created successfully');

      if (createOrderSuccessData.warnings && createOrderSuccessData.warnings.length > 0) {
        setWarnings(createOrderSuccessData.warnings);
        setWarningDialogOpen(true);
      } else if (createOrderSuccessData.data && createOrderSuccessData.data.warnings && createOrderSuccessData.data.warnings.length > 0) {
        setWarnings(createOrderSuccessData.data.warnings);
        setWarningDialogOpen(true);
      }
    }
    if (createOrderError) {
      toast.error(createOrderError.message || 'Failed to create order');
    }
    return () => {
      dispatch(clearCreateOrderData());
    };
  }, [createOrderSuccessData, createOrderError, dispatch]);

  useEffect(() => {
    if (updateOrderSuccessData) {
      dispatch(getOrderAction());
      toast.success('Order updated successfully');
    }
    if (updateOrderError) {
      toast.error(updateOrderError.message || 'Failed to update order');
    }
    return () => {
      dispatch(clearupdateOrderAction());
    };
  }, [updateOrderSuccessData, updateOrderError, dispatch]);

  useEffect(() => {
    if (deleteOrderSuccessData) {
      dispatch(getOrderAction());
      toast.success('Order deleted successfully');
    }
    if (deleteOrderError) {
      toast.error(deleteOrderError.message || 'Failed to delete order');
    }
    return () => {
      dispatch(cleardeleteOrderAction());
    };
  }, [deleteOrderSuccessData, deleteOrderError, dispatch]);

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Filter and sort orders
  const filteredOrders = ordersData?.data ? ordersData.data
    .filter(order =>
      // Apply text search filter
      ((order.client_name && order.client_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (order.id && order.id.toString().includes(searchTerm))) &&
      // Apply status filter
      (statusFilter === 'All' || order.status === statusFilter)
    )
    .sort((a, b) => {
      if (sortOrder === 'asc') {
        return new Date(a.createdAt) - new Date(b.createdAt);
      }
      return new Date(b.createdAt) - new Date(a.createdAt);
    }) : [];

  // Get machines for dropdown
  const machines = machinesData?.data || [];

  // Get unique statuses for filter dropdown
  const statuses = ordersData?.data
    ? ['All', ...Array.from(new Set([...ordersData.data.map(order => order.status), 'Delivered'])).sort()]
    : ['All', 'Completed', 'Delivered', 'In Progress', 'Pending'].sort(); // Fallback statuses

  useEffect(() => {
    dispatch(getOrderAction());
  }, [dispatch]);


  const statusColors = {
    Pending: { borderColor: "#FF0000", textColor: "#FF0000" },
    Progress: { borderColor: "#FFA500", textColor: "#FFA500" },
    Manufactured: { borderColor: "#FFD700", textColor: "#FFD700" },
    Tested: { borderColor: "#008000", textColor: "#008000" },
    Delivered: { borderColor: "#0000FF", textColor: "#0000FF" },
  };

  const calculateStatusStyle = (status) => {
    const statusStyle = statusColors[status] || { borderColor: "#CCCCCC", textColor: "#CCCCCC" }; // Default fallback
    return statusStyle;
  };

  return (
    <Box sx={{ width: { xs: '100%', sm: '80%' }, ml: { xs: '0%', sm: '10%', md: '20%' }, mt: { xs: '13%', sm: '10%', md: '7%' } }}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{ padding: '24px' }}
      >
        <Box sx={{ mb: 4 }}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ml:{ xs: '0%', sm: '4%', md: '5%' }}}>
              <h1 style={{ fontWeight: "bold", fontSize: "2rem", fontFamily: "Poppins" }}>
                Orders Management
              </h1>
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  placeholder="Search"
                  variant="outlined"
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search color="action" />
                      </InputAdornment>
                    ),
                  }}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel id="status-filter-label">Filter by Status</InputLabel>
                  <Select
                    labelId="status-filter-label"
                    id="status-filter"
                    value={statusFilter}
                    onChange={handleStatusFilterChange}
                    label="Filter by Status"
                    startAdornment={
                      <InputAdornment position="start">
                        <FilterList fontSize="small" />
                      </InputAdornment>
                    }
                  >
                    {statuses.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={4}>
                <Button
                  variant="contained"
                  onClick={handleCreateOrder}
                  fullWidth
                  sx={{
                    backgroundColor: '#1814F3',
                    color: '#fff',
                    borderRadius: '10px',
                    fontFamily: 'Inter',
                  }}
                >
                  Create New Order
                </Button>
              </Grid>
            </Grid>

            {/* Conditional rendering for table or cards */}
            {isMobile ? (
              <Grid container spacing={2}>
                {filteredOrders.map((order) => {
                  const statusStyle = calculateStatusStyle(order.status);
                  return (
                    <Grid item xs={12} sm={6} key={order.id}>
                      <Card variant="outlined" sx={{ borderRadius: '10px', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',}}>
                        <CardContent>
                          <Typography variant="h6" sx={{ fontFamily: 'Poppins', fontWeight: 'bold' }}>
                            Order #{order.id}
                          </Typography>
                          <Typography sx={{ fontFamily: 'Poppins', color: '#292D32' }}>
                            Client: {order.client_name}
                          </Typography>
                          <Typography sx={{ fontFamily: 'Poppins', color: '#292D32' }}>
                            Product: {order.Machine.name}
                          </Typography>
                          <Typography sx={{ fontFamily: 'Poppins', color: '#292D32' }}>
                            Quantity: {order.quantity}
                          </Typography>
                          <Typography sx={{ fontFamily: 'Poppins', color: '#292D32', mt: 1 }}>
                            Status:
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-block',
                                border: `1px solid ${statusStyle.borderColor}`,
                                borderRadius: '15px',
                                padding: '2px 8px',
                                color: statusStyle.textColor,
                                fontFamily: 'Poppins',
                                ml: 1,
                              }}
                            >
                              {order.status}
                            </Box>
                          </Typography>
                          <Typography sx={{ fontFamily: 'Poppins', color: '#292D32', mt: 1 }}>
                            Created: {formatDate(order.createdAt)}
                          </Typography>
                          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                            <Button
                              variant="contained"
                              onClick={() => handleEditOrder(order)}
                              sx={{
                                textTransform: 'none',
                                backgroundColor: '#1814F3',
                                color: '#fff',
                                borderRadius: '10px',
                                fontFamily: 'Inter',
                              }}
                            >
                              Edit
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <TableContainer component={Paper} sx={{
                boxShadow: 'none',
                '& .MuiTable-root': {
                  borderCollapse: 'collapse'
                },
                mt: '2%',ml: '5%', mr: '3%',
                maxWidth: '95%',
              }}>
                <Table sx={{
                  minWidth: 650,
                  '& .MuiTableCell-root': {
                    borderBottom: '1px solid #E6EFF5',
                    borderTop: 'none',
                    borderLeft: 'none',
                    borderRight: 'none'
                  },
                  '& .MuiTableHead-root .MuiTableCell-root': {
                    borderBottom: '1px solid #E6EFF5'
                  }
                }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: '#B5B7C0', fontFamily: 'Poppins' }}>Order ID</TableCell>
                      <TableCell sx={{ color: '#B5B7C0', fontFamily: 'Poppins' }}>Client Name</TableCell>
                      <TableCell sx={{ color: '#B5B7C0', fontFamily: 'Poppins' }}>Product Name</TableCell>
                      <TableCell sx={{ color: '#B5B7C0', fontFamily: 'Poppins' }}>Quantity</TableCell>
                      <TableCell sx={{ color: '#B5B7C0', fontFamily: 'Poppins' }}>Status</TableCell>
                      <TableCell sx={{ color: '#B5B7C0', fontFamily: 'Poppins' }}>Created Date</TableCell>
                      <TableCell sx={{ color: '#B5B7C0', fontFamily: 'Poppins' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <AnimatePresence>
                      {filteredOrders.map((order, index) => {
                        const statusStyle = calculateStatusStyle(order.status);
                        return (
                          <motion.tr
                            key={order.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ delay: index * 0.1 }}
                            component={TableRow}
                          >
                            <TableCell sx={{ color: '#292D32', fontFamily: 'Poppins' }}>#{order.id}</TableCell>
                            <TableCell sx={{ color: '#292D32', fontFamily: 'Poppins' }}>{order.client_name}</TableCell>
                            <TableCell sx={{ color: '#292D32', fontFamily: 'Poppins' }}>{order.Machine.name}</TableCell>
                            <TableCell sx={{ color: '#292D32', fontFamily: 'Poppins' }}>{order.quantity}</TableCell>
                            <TableCell>
                              <Box>
                                <Button
                                  variant="outlined"
                                  sx={{
                                    borderColor: statusColors[order.status]?.borderColor || "#1976D2",
                                    color: statusColors[order.status]?.textColor || "#1976D2",
                                    borderRadius: 5,
                                  }}
                                >
                                  {order.status}
                                </Button>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ color: '#292D32', fontFamily: 'Poppins' }}>{formatDate(order.createdAt)}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                  variant="contained"
                                  onClick={() => handleEditOrder(order)}
                                  sx={{
                                    textTransform: 'none',
                                    backgroundColor: '#1814F3',
                                    color: '#fff',
                                    borderRadius: '10px',
                                    fontFamily: 'Inter',
                                  }}
                                >
                                  Edit
                                </Button>
                              </Box>
                            </TableCell>
                          </motion.tr>
                        );
                      })}
                    </AnimatePresence>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>
      </motion.div>

      {/* Modal for Order Creation/Editing */}
      <Dialog open={isModalOpen} onClose={handleModalClose}>
        <DialogTitle>{isEditing ? 'Edit Order' : 'Create New Order'}</DialogTitle>
        <DialogContent>
          {errorMessage && (
            <Typography color="error" sx={{ marginBottom: 2 }}>
              {errorMessage}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column', minWidth: '400px', mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel id="machine-select-label">Product</InputLabel>
              <Select
                labelId="machine-select-label"
                id="machine-select"
                value={newOrder.machineId}
                label="Machine"
                onChange={(e) => setNewOrder({ ...newOrder, machineId: e.target.value })}
              >
                {machines.map((machine) => (
                  <MenuItem key={machine.id} value={machine.id.toString()}>
                    {machine.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Client Name"
              value={newOrder.clientName}
              onChange={(e) => setNewOrder({ ...newOrder, clientName: e.target.value })}
              margin="dense"
            />

            <TextField
              fullWidth
              label="Quantity"
              type="number"
              value={newOrder.quantity}
              onChange={(e) => setNewOrder({ ...newOrder, quantity: e.target.value })}
              margin="dense"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModalClose} color="secondary">Cancel</Button>
          <Button onClick={handleSaveOrder} color="primary">{isEditing ? 'Update' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* dialog for warnings */}
      <Dialog
        open={warningDialogOpen}
        onClose={() => setWarningDialogOpen(false)}
        PaperProps={{
          style: { maxWidth: '600px', width: '100%', maxHeight: '80vh' }
        }} fullWidth
      >
        <DialogTitle sx={{ color: '#f59e0b', display: 'flex', alignItems: 'center', backgroundColor: '#fffbeb', borderBottom: '1px solid #fef3c7' }}>
          <span style={{ marginRight: '8px', fontSize: '24px' }}>⚠️</span> Stock Warnings
        </DialogTitle>
        <DialogContent
          sx={{
            py: 2,
            maxHeight: '60vh',
            overflowY: 'auto'
          }}
        >
          {warnings && warnings.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                The following warnings were generated during order processing:
              </Typography>
              {warnings.map((warning, index) => {
                return (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderLeft: '4px solid #f59e0b',
                      backgroundColor: '#fffbeb',
                      borderRadius: '4px'
                    }}
                  >
                    <Typography variant="body2" color="#92400e">
                      {warning}
                    </Typography>
                  </Paper>
                );
              })}
            </Box>
          ) : (
            <Typography>No specific warnings to display.</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #fef3c7', p: 2 }}>
          <Button
            onClick={() => setWarningDialogOpen(false)}
            variant="contained"
            sx={{
              backgroundColor: '#f59e0b',
              '&:hover': {
                backgroundColor: '#d97706',
              },
              color: 'white',
              fontWeight: 'bold'
            }}
          >
            Acknowledge Warnings
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrdersManagement;

